import { TextView } from '@st/document-annotations'
import { HTMLSurface, measureText, Viewport } from '@st/document-rendering'
import {
  ElementStatus,
  PageStructure,
  SelectedElementContainer,
  ToolMessage,
  useLinearTransform,
  ViewElementContainer
} from '@st/document-viewer-kit'
import { toDragPointerEvent, useUpdateBounds } from '@st/document-viewer-kit/src/use-movable'
import { Size } from '@st/geom'
import { TextAnnotation } from '@st/sdk'
import { FontSizeLargerIcon, FontSizeSmallerIcon } from '@st/theme'
import { THEME_COLORS } from '@st/theme/colors'
import { uuidv7 } from '@st/util/uuidv7'
import { DeleteIcon } from '~/components/icons'
import { TextToolState } from '../../annotations'
import { AnnotationFloatingToolbar, FloatingToolbarButton } from '../../floating-toolbar'
import { InlineColorPicker } from '../../inline-color-picker'
const LINE_HEIGHT = 1.1

export const TEXT_COLOR_OPTIONS = [
  THEME_COLORS['red'][600],
  THEME_COLORS['blue'][500],
  THEME_COLORS['orange'][500],
  THEME_COLORS['green'][500],
  THEME_COLORS['gray'][900]
]

export function TextToolEditor({
  toolState,
  activeAnnotation,
  send
}: {
  toolState: TextToolState
  activeAnnotation: TextAnnotation | undefined
  send: (message: ToolMessage<TextAnnotation, TextToolState>) => void
}) {
  return (
    <InlineColorPicker
      options={TEXT_COLOR_OPTIONS}
      value={toolState.settings.color}
      onChange={(color) => {
        if (activeAnnotation) {
          const newAnnotation: TextAnnotation = {
            ...activeAnnotation,
            color: color
          }
          send({ type: 'updateAnnotationDraft', annotationDraft: newAnnotation })
          send({ type: 'updateTool', tool: copyToolState(newAnnotation) })
        } else {
          send({
            type: 'updateTool',
            tool: { ...toolState, settings: { ...toolState.settings, color: color } }
          })
        }
      }}
    />
  )
}

export function copyToolState(annotation: TextAnnotation): TextToolState {
  return {
    name: 'text',
    settings: {
      color: annotation.color,
      font: annotation.font
    }
  }
}

export function TextToolOverlay({
  page,
  toolState,
  activeAnnotation,
  status,
  send
}: {
  page: PageStructure
  toolState: TextToolState
  activeAnnotation: TextAnnotation | undefined
  status: ElementStatus
  send: (message: ToolMessage<TextAnnotation, TextToolState>) => void
}) {
  const transform = useLinearTransform()

  return (
    <div
      style={{
        position: 'absolute',
        cursor: 'text',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.0)'
      }}
      onPointerDown={(e) => {
        e.preventDefault()
        e.stopPropagation()

        const pointerEvent = toDragPointerEvent(e, transform, 'drawing-canvas')

        if (activeAnnotation) {
          send({ type: 'deselectAnnotation' })
          return
        } else {
          send({
            type: 'createAnnotationDraft',
            elementStatus: 'editing',
            annotationDraft: autoResizeAnnotation(
              {
                id: uuidv7(),
                time: new Date().toISOString(),
                type: 'text',
                pageNumber: page.pageNumber,
                content: '',
                bounds: {
                  x: pointerEvent.point.x,
                  y: pointerEvent.point.y,
                  width: 0,
                  height: 0
                },
                color: toolState.settings.color,
                font: toolState.settings.font
              },
              { lineHeight: LINE_HEIGHT }
            )
          })
        }
      }}
    />
  )
}

export function TextAnnotationElement({
  annotation,
  page,
  status,
  send
}: {
  annotation: TextAnnotation
  page: PageStructure
  status: ElementStatus
  send: (message: ToolMessage<TextAnnotation, TextToolState>) => void
}) {
  switch (status) {
    case 'selected':
      return <TextAnnotationSelected annotation={annotation} send={send} />
    case 'editing':
      return <TextAnnotationEditing annotation={annotation} send={send} />
    case 'none':
      return (
        <TextAnnotationView
          annotation={annotation}
          onSelect={() => send({ type: 'selectAnnotation', annotationId: annotation.id })}
        />
      )
  }
}

function TextAnnotationView({
  annotation,
  onSelect
}: {
  annotation: TextAnnotation
  onSelect: () => void
}) {
  return (
    <ViewElementContainer key={annotation.id} bounds={annotation.bounds} onClick={onSelect}>
      <TextAnnotationDisplay annotation={annotation} />
    </ViewElementContainer>
  )
}

function TextAnnotationDisplay({
  annotation,
  onDoubleClick
}: {
  annotation: TextAnnotation
  onDoubleClick?: () => void
}) {
  return (
    <div
      style={{
        position: 'relative',
        width: annotation.bounds.width,
        height: annotation.bounds.height
      }}
      onDoubleClick={onDoubleClick}
    >
      <Viewport
        Surface={HTMLSurface}
        width={annotation.bounds.width}
        height={annotation.bounds.height}
      >
        <TextView annotation={annotation} />
      </Viewport>
    </div>
  )
}

function TextAnnotationSelected({
  annotation,
  send
}: {
  annotation: TextAnnotation
  send: (message: ToolMessage<TextAnnotation, TextToolState>) => void
}) {
  const { onMove, onResize } = useUpdateBounds({
    initialBounds: annotation.bounds,
    onUpdate: (event) => {
      send({
        type: 'updateAnnotationDraft',
        annotationDraft: { ...annotation, bounds: event.newBounds }
      })
    },
    onCommit: (event) => {
      send({
        type: 'updateAnnotationDraft',
        annotationDraft: { ...annotation, bounds: event.newBounds }
      })
    }
  })

  return (
    <SelectedElementContainer
      bounds={annotation.bounds}
      onMove={onMove}
      onResize={onResize}
      resizable={false}
      onDoubleClick={() => {
        send({ type: 'beginEditingAnnotation', annotationDraft: annotation })
      }}
      toolbar={
        <AnnotationFloatingToolbar>
          <FloatingToolbarButton
            icon={<FontSizeSmallerIcon className="size=6" />}
            onClick={() => {
              const newAnnotation: TextAnnotation = autoResizeAnnotation(
                {
                  ...annotation,
                  font: { ...annotation.font, size: annotation.font.size - 2 }
                },
                { lineHeight: LINE_HEIGHT }
              )
              send({
                type: 'updateAnnotationDraft',
                annotationDraft: newAnnotation
              })
              send({ type: 'updateTool', tool: copyToolState(newAnnotation) })
            }}
          />
          <FloatingToolbarButton
            icon={<FontSizeLargerIcon className="size=6" />}
            onClick={() => {
              const newAnnotation: TextAnnotation = autoResizeAnnotation(
                {
                  ...annotation,
                  font: { ...annotation.font, size: annotation.font.size + 2 }
                },
                { lineHeight: LINE_HEIGHT }
              )

              send({
                type: 'updateAnnotationDraft',
                annotationDraft: newAnnotation
              })
              send({ type: 'updateTool', tool: copyToolState(newAnnotation) })
            }}
          />
          <FloatingToolbarButton
            icon={<DeleteIcon className="size-6" />}
            onClick={() => send({ type: 'deleteAnnotation', annotationId: annotation.id })}
          />
        </AnnotationFloatingToolbar>
      }
    >
      <TextAnnotationDisplay annotation={annotation} />
    </SelectedElementContainer>
  )
}

function TextAnnotationEditing({
  annotation,
  send
}: {
  annotation: TextAnnotation
  send: (message: ToolMessage<TextAnnotation, TextToolState>) => void
}) {
  return (
    <ViewElementContainer key={annotation.id} bounds={annotation.bounds}>
      <textarea
        value={annotation.content}
        autoFocus={true}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        wrap="off"
        style={{
          border: 'none',
          padding: 0,
          outline: 'none',
          background: 'none',
          resize: 'none',
          overflow: 'hidden',
          fontKerning: 'none',
          boxSizing: 'content-box',
          whiteSpace: 'pre',
          width: annotation.bounds.width,
          height: annotation.bounds.height,
          fontFamily: annotation.font.family,
          fontSize: annotation.font.size,
          color: annotation.color,
          lineHeight: LINE_HEIGHT
        }}
        onChange={(e) => {
          const newText = e.target.value

          const nextAnnotation: TextAnnotation = autoResizeAnnotation(
            {
              ...annotation,
              content: newText
            },
            { lineHeight: LINE_HEIGHT }
          )
          send({
            type: 'updateAnnotationDraft',
            annotationDraft: nextAnnotation
          })
        }}
      />
    </ViewElementContainer>
  )
}

function calculateTextDimensions(
  text: string,
  {
    font,
    lineHeight
  }: {
    font: { family: string; size: number; weight: 'normal' | 'bold' }
    lineHeight: number
  }
): Size {
  return measureText(text.length == 0 ? '  ' : text, {
    font: { family: font.family, weight: font.weight, size: font.size },
    lineHeight: lineHeight
  })
}

function autoResizeAnnotation(
  annot: TextAnnotation,
  { lineHeight }: { lineHeight: number }
): TextAnnotation {
  const size = calculateTextDimensions(annot.content, {
    font: annot.font,
    lineHeight: lineHeight
  })
  return {
    ...annot,
    bounds: {
      x: annot.bounds.x,
      y: annot.bounds.y,
      width: size.width,
      height: size.height
    }
  }
}
